import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { TruckIcon, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { toast } from 'sonner';

const API_URL = API_BASE;

export const Vehicles = () => {const { t } = useTranslation("ezparcel");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    plate: '',
    type: '',
    capacity: '',
    volumeCapacity: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const response = await fetch(`${API_URL}/api/ezparcel/vehicles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setVehicles(data.map((v) => ({
            id: v.vehicle_id || v.id,
            make: v.make,
            model: v.model,
            plate: v.plate,
            type: v.type,
            status: v.status || 'available',
            fuelLevel: v.fuel_level || 75,
            mileage: v.mileage || 0,
            capacity: v.capacity
          })));
        }
      } catch (err) {
        console.error('Failed to fetch vehicles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  const handleAddVehicle = async () => {
    if (!newVehicle.make || !newVehicle.model || !newVehicle.plate || !newVehicle.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ezparcel/vehicles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
        },
        body: JSON.stringify({
          make: newVehicle.make,
          model: newVehicle.model,
          plate: newVehicle.plate,
          type: newVehicle.type,
          capacity: newVehicle.capacity || '1200 kg',
          volume_capacity: newVehicle.volumeCapacity || '10 m³'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add vehicle');
      }

      const savedVehicle = await response.json();

      // Convert API response to frontend format
      const vehicle = {
        id: savedVehicle.id,
        plate: savedVehicle.plate,
        make: savedVehicle.make,
        model: savedVehicle.model,
        type: savedVehicle.type,
        status: savedVehicle.status,
        capacity: savedVehicle.capacity,
        volumeCapacity: savedVehicle.volume_capacity,
        currentLoad: savedVehicle.current_load,
        currentDriver: savedVehicle.current_driver,
        lastMaintenance: savedVehicle.last_maintenance,
        nextMaintenance: savedVehicle.next_maintenance
      };

      setVehicles([vehicle, ...vehicles]);
      setNewVehicle({ make: '', model: '', plate: '', type: '', capacity: '', volumeCapacity: '' });
      setIsAddDialogOpen(false);
      toast.success(`Vehicle "${newVehicle.make} ${newVehicle.model}" added successfully!`);
    } catch (error) {
      console.error('Error adding vehicle:', error);
      toast.error('Failed to add vehicle. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("fleetManagement", "Fleet Management")}</h1>
          <p className="text-muted-foreground mt-2">{t("monitorVehicleStatus", "Monitor vehicle status, capacity, and maintenance schedules")}

          </p>
        </div>
        <Button
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="add-vehicle-btn">
          
          <Plus className="mr-2 h-4 w-4" />{t("addVehicle", "Add Vehicle")}

        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {vehicles.map((vehicle) => {
          const loadPercentage = vehicle.currentLoad / parseInt(vehicle.capacity) * 100;

          return (
            <Card key={vehicle.id} className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--primary)/.1)]">
                      <TruckIcon className="h-6 w-6 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{vehicle.make} {vehicle.model}</h3>
                      <p className="text-sm text-muted-foreground">{vehicle.plate}</p>
                    </div>
                  </div>
                  <Badge
                    className={`status-badge ${
                    vehicle.status === 'in_use' ?
                    'status-in-progress' :
                    vehicle.status === 'available' ?
                    'status-completed' :
                    'bg-gray-100 text-gray-700'}`
                    }>
                    
                    {vehicle.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">{t("vehicleId", "Vehicle ID")}</p>
                      <p className="font-medium text-sm mt-1">{vehicle.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("type", "Type")}</p>
                      <p className="font-medium text-sm mt-1">{vehicle.type}</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">{t("currentLoad", "Current Load")}</span>
                      <span className="font-medium">
                        {vehicle.currentLoad} / {vehicle.capacity}
                      </span>
                    </div>
                    <Progress value={loadPercentage} className="h-2" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">{t("capacity", "Capacity")}</p>
                      <p className="font-medium text-sm mt-1">{vehicle.capacity}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("volume", "Volume")}</p>
                      <p className="font-medium text-sm mt-1">{vehicle.volumeCapacity}</p>
                    </div>
                  </div>

                  {vehicle.currentDriver &&
                  <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground">{t("assignedDriver", "Assigned Driver")}</p>
                      <p className="text-sm font-medium mt-1">{vehicle.currentDriver}</p>
                    </div>
                  }

                  <div className="pt-3 border-t border-border">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-muted-foreground">{t("lastMaintenance", "Last Maintenance")}</p>
                        <p className="font-medium mt-1">{vehicle.lastMaintenance}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{t("nextMaintenance", "Next Maintenance")}</p>
                        <p className="font-medium mt-1">{vehicle.nextMaintenance}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1">{t("viewDetails", "View Details")}

                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">{t("assign", "Assign")}

                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>);

        })}
      </div>

      {/* Add Vehicle Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TruckIcon className="h-5 w-5" />{t("addNewVehicle", "Add New Vehicle")}

            </DialogTitle>
            <DialogDescription>{t("enterTheVehicles", "Enter the vehicle's information to add it to the fleet.")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="vehicle-make">{t("make", "Make *")}</Label>
                <Input
                  id="vehicle-make"
                  placeholder="Ford"
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="vehicle-model">{t("model", "Model *")}</Label>
                <Input
                  id="vehicle-model"
                  placeholder="Transit"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })} />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="vehicle-plate">{t("licensePlate", "License Plate *")}</Label>
                <Input
                  id="vehicle-plate"
                  placeholder="ABC-1234"
                  value={newVehicle.plate}
                  onChange={(e) => setNewVehicle({ ...newVehicle, plate: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="vehicle-type">{t("vehicleType", "Vehicle Type *")}</Label>
                <Select
                  value={newVehicle.type}
                  onValueChange={(value) => setNewVehicle({ ...newVehicle, type: value })}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Van">{t("van", "Van")}</SelectItem>
                    <SelectItem value="Truck">{t("truck", "Truck")}</SelectItem>
                    <SelectItem value="Motorcycle">{t("motorcycle", "Motorcycle")}</SelectItem>
                    <SelectItem value="Car">{t("car", "Car")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="vehicle-capacity">{t("weightCapacity", "Weight Capacity")}</Label>
                <Input
                  id="vehicle-capacity"
                  placeholder="1200 kg"
                  value={newVehicle.capacity}
                  onChange={(e) => setNewVehicle({ ...newVehicle, capacity: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="vehicle-volume">{t("volumeCapacity", "Volume Capacity")}</Label>
                <Input
                  id="vehicle-volume"
                  placeholder="10 m³"
                  value={newVehicle.volumeCapacity}
                  onChange={(e) => setNewVehicle({ ...newVehicle, volumeCapacity: e.target.value })} />
                
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleAddVehicle}
              disabled={isLoading}
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
              
              {isLoading ? 'Adding...' : 'Add Vehicle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};